// rag-service ⇆ console KB shape adapter.
//
// rag-service speaks a NATIVE KB contract (see rag-types.ts). The console KB screens consume the
// legacy aiassist shape (`RKnowledgeBase`, `RKnowledgeBaseDocument`, etc. in ./../../pages/flow/
// knowledge-base/types). This module is the SINGLE place that translates between the two, so the
// screens stay byte-for-byte unchanged after the repoint. Every function here is pure (no I/O):
// flow-service.ts composes them with rag-kb-service.ts (the HTTP layer) and the `{ data }` envelope.
//
// The one field rag cannot supply is `phlo_uuids` (the KB↔agent-flow association — owned by
// PHLO-core, not rag). It is threaded in as an explicit argument so the mapping stays pure; the
// wiring layer sources it from PHLO-core and passes it here.

import type {
  RagAppData,
  RagCrawlStatusResponse,
  RagItemStatus,
  RagKbStatus,
  RagKbStatusDocument,
  RagKbStatusResponse,
  RagKbStatusSource,
  RagKnowledgeBaseCreateRequest,
  RagKnowledgeBaseResponse,
  RagMcpArticle,
  RagMcpArticleCollection,
  RagMcpSource,
  RagRollupStatus,
  RagSourceConfig,
  RagSourceSpec,
  RagSourceType,
  RagUpdateKnowledgeBaseRequest,
} from 'v2/pages/flow/knowledge-base/rag-types'
import type {
  AddDocumentPayload,
  CreateKBPayload,
  CreateKBSource,
  DocumentStatus,
  KBAppDocument,
  KBDocument,
  KBStatus,
  McpArticle,
  McpArticleCollection,
  RCrawlSiteStatus,
  RKnowledgeBase,
  RKnowledgeBaseDocument,
  SyncFrequencyPeriod,
} from 'v2/pages/flow/knowledge-base/types'
import { KBDocumentSource } from 'v2/pages/flow/knowledge-base/types'

// ── status mapping ────────────────────────────────────────────────────────────────────────────

// rag raw per-document status → console DocumentStatus.
// rag never emits `completed_with_errors` for a SINGLE document (it is a rollup-only value).
const DOC_STATUS: Record<RagItemStatus, DocumentStatus> = {
  pending: 'queued',
  fetching: 'syncing',
  ingesting: 'syncing',
  done: 'completed',
  error: 'failed',
  empty: 'completed', // benign no-content page — terminal, NOT a failure (surfaced via empty_count)
}

export const mapDocStatus = (s: RagItemStatus): DocumentStatus => DOC_STATUS[s] ?? 'syncing'

// rag rollup (`embedding_status`) already uses the exact strings the console badge understands, so
// this is a pass-through. The return type is the wider DocumentStatus because a source/KB rollup CAN
// be `completed_with_errors` — which the detail badge renders (success + warning icon). The console's
// source/KB field is typed KBStatus (narrower), so callers cast on assignment; the runtime value is
// honest and the badge handles it.
export const mapRollupStatus = (s: RagRollupStatus): DocumentStatus => s

// KB lifecycle status (the list summary, which has no tree to roll up) → console KBStatus.
const KB_LIFECYCLE: Record<RagKbStatus, KBStatus> = {
  provisioning: 'syncing',
  ingesting: 'syncing',
  ready: 'completed',
  error: 'failed',
}

export const mapKbLifecycleStatus = (s: RagKbStatus): KBStatus => KB_LIFECYCLE[s] ?? 'syncing'

// Ingest/embedding status → a console Badge {label, variant}. This is the READINESS of the KB's
// content (is it still crawling/embedding, done, or failed) — distinct from the `enabled` "State"
// column (Active/Disabled). Surfacing it fixes the long-standing "shows Active before anything has
// been ingested" confusion: a brand-new/ingesting KB now reads "Syncing", not a ready-looking badge.
export const kbStatusBadge = (
  status: DocumentStatus | null | undefined,
): { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' } => {
  switch (status) {
    case 'completed':
      return { label: 'Ready', variant: 'success' }
    case 'completed_with_errors':
      return { label: 'Ready (partial)', variant: 'warning' }
    case 'failed':
      return { label: 'Failed', variant: 'destructive' }
    case 'queued':
      return { label: 'Queued', variant: 'secondary' }
    default:
      return { label: 'Syncing', variant: 'warning' }
  }
}

// ── source type mapping ─────────────────────────────────────────────────────────────────────────

const SOURCE_TYPE: Record<RagSourceType, KBDocumentSource> = {
  url: KBDocumentSource.URL,
  file: KBDocumentSource.FILE,
  app: KBDocumentSource.APP,
  raw: KBDocumentSource.FILE, // raw inline text has no dedicated console source kind → render as a file
}

export const mapSourceType = (t: RagSourceType): KBDocumentSource =>
  SOURCE_TYPE[t] ?? KBDocumentSource.FILE

// ── sync cadence ──────────────────────────────────────────────────────────────────────────────

const PERIOD_SECONDS: Record<SyncFrequencyPeriod, number> = {
  WEEK: 7 * 24 * 3600,
  MONTH: 30 * 24 * 3600,
}

// (period, frequency) → rag's sync_interval_seconds. rag clamps to 300..2592000.
export const syncPeriodToSeconds = (period: SyncFrequencyPeriod, frequency: number): number =>
  (PERIOD_SECONDS[period] ?? PERIOD_SECONDS.WEEK) * Math.max(1, frequency || 1)

// ── status-tree → console shapes ─────────────────────────────────────────────────────────────

const ragConfigToConsole = (
  config: RagSourceConfig,
  sourceType: KBDocumentSource,
): RKnowledgeBase['sources'][0]['config'] => ({
  source_type: config.type ? mapSourceType(config.type) : sourceType,
  url: config.ref ?? '',
  original_filename: config.original_filename ?? null,
  max_depth: config.max_depth ?? 0,
  max_pages: config.max_pages ?? 0,
  crawl_subpages: config.crawl_subpages ?? false,
  exclude_paths: config.exclude_paths ?? [],
  include_paths: config.include_paths ?? [],
  app_source: config.app_source ?? null,
  app_source_id: config.app_source_id ?? null,
  app_data: (config.app_data ?? null) as KBAppDocument[] | null,
})

const ragDocToConsole = (
  doc: RagKbStatusDocument,
  sourceType: KBDocumentSource,
): RKnowledgeBaseDocument => ({
  id: doc.doc_uuid,
  document_type: sourceType as KBDocumentSource.URL, // console field is over-narrow; value is the real source type
  name: doc.name ?? '',
  source: null,
  remote_id: null,
  embedding_status: mapDocStatus(doc.status),
  display_name: doc.name || doc.ref || '',
  enabled: doc.enabled,
  updated_at: doc.updated_at ?? '',
  updated_by: doc.updated_by ?? { uuid: '', name: '' },
  last_sync_attempt_at: doc.last_synced_at ?? '',
  last_synced_successfully_at: doc.last_synced_at ?? '',
  embedding_error: doc.error_detail ?? null, // surface the per-document failure cause in the UI
  content_hash: doc.content_hash ?? '',
  chunk_count: doc.chunk_count ?? 0,
  is_empty: doc.status === 'empty', // benign no-content page — not a 0-chunk embed FAILURE

  // The fetchable file URL for the Download action. Only file sources have a real downloadable URL
  // (url=page, app=remote id are not files), so only expose it there.
  download_url: sourceType === KBDocumentSource.FILE ? (doc.ref ?? null) : null,
})

const ragSourceToConsole = (src: RagKbStatusSource): RKnowledgeBase['sources'][0] => {
  const sourceType = mapSourceType(src.type)
  return {
    id: src.id,
    source_type: sourceType,
    documents: (src.documents ?? []).map((d) => ragDocToConsole(d, sourceType)),
    // TRUE totals (documents above is capped top-N, errored-first). Fall back to the returned length
    // when the backend doesn't send them (older rag) so the count never reads 0 spuriously.
    document_count: src.document_count ?? (src.documents ?? []).length,
    error_count: src.error_count ?? 0,
    empty_count: src.empty_count ?? 0,
    enabled: src.enabled,
    embedding_status: mapRollupStatus(src.embedding_status) as KBStatus,
    fetching: src.fetching,
    error_detail: src.error_detail ?? null, // source-level failure cause (e.g. file errored before any doc)
    config: ragConfigToConsole(src.config ?? {}, sourceType),
  }
}

// Chunk-aware KB status used by BOTH the list and detail views so they never disagree. See the
// call-site comment for the two corrections (ready-but-empty → failed; error-with-chunks → partial).
const kbEmbeddingStatus = (
  summary: RagKnowledgeBaseResponse,
  tree?: RagKbStatusResponse | null,
): KBStatus => {
  const chunks = summary.chunk_count ?? 0
  const base = tree ? mapRollupStatus(tree.embedding_status) : mapKbLifecycleStatus(summary.status)
  // "ready but embedded nothing" = failed — but ONLY if at least one source actually tried. A
  // brand-new empty KB (no sources yet) is "ready/completed" with 0 chunks and must NOT read Failed.
  const hasSources = (tree?.sources?.length ?? summary.source_count ?? 0) > 0
  if (base === 'completed' && chunks === 0 && hasSources) return 'failed'
  if (summary.status === 'error' && chunks > 0) return 'completed_with_errors' as KBStatus // partial
  return base as KBStatus
}

// Status-tree existence marker for getKnowledgeBase's tree-attach. GET /kb/{id}/status is configured
// to RESOLVE (not throw) on 404 → a body with no fields; a real 200 carries the KB. SER-5976 (backend
// #256) made that endpoint uuid-only — the 200 body is keyed by `uuid`, no longer by the sequential
// integer `kb_id`. So a present `uuid` marks a real tree to attach; its absence (a 404-resolved empty
// body, or null on error) yields null → ragKbToConsole falls back to the summary's lifecycle status
// instead of mapping undefined rollup fields. Colocated with ragKbToConsole (its sole consumer) so the
// marker is unit-tested rather than buried inline in the service.
export const statusTreeIfPresent = (
  treeResp?: RagKbStatusResponse | null,
): RagKbStatusResponse | null => (treeResp?.uuid ? treeResp : null)

// Combine rag's KB summary (GET /kb/{id}) with — when available — its status tree
// (GET /kb/{id}/status) into the console-facing RKnowledgeBase. For the list view the tree is
// omitted (sources/documents empty, status derived from the summary lifecycle status).
export const ragKbToConsole = (
  summary: RagKnowledgeBaseResponse,
  tree?: RagKbStatusResponse | null,
  phloUuids: string[] = [],
): RKnowledgeBase => {
  const sources = (tree?.sources ?? []).map(ragSourceToConsole)
  return {
    // SER-5976: the console-facing KB id is the rag `uuid` (stable, non-enumerable) — the value the
    // picker offers, that flow-save stores in `knowledge_base_ids`, that KB detail links deep-link by,
    // and that the KB↔agent-flow reverse link (owned by PHLO-core) keys on. rag accepts this uuid on
    // every `/kb/{id}` route. The sequential integer `kb_id` is no longer returned by rag, so there is
    // no fallback: the uuid is the id.
    id: summary.uuid,
    name: summary.name,
    phlo_uuids: phloUuids,
    enabled: tree?.enabled ?? summary.enabled,
    updated_at: tree?.updated_at ?? summary.updated_at ?? '',
    updated_by: tree?.updated_by ?? summary.updated_by ?? { uuid: '', name: '' },
    include_youtube_content: summary.include_youtube_content,
    include_image_content: summary.include_image_content,
    url_selection_type: summary.url_selection_type,
    sync_period: (summary.sync_period as SyncFrequencyPeriod) ?? 'WEEK',
    sync_frequency: summary.sync_frequency ?? 1,
    sync_enabled: summary.sync_enabled,
    // KB-wide embedded-chunk total (summed across sources). Climbs every ingest batch → the detail
    // poller's uncapped progress signal (the per-source documents[] is capped and stops changing).
    chunk_count: summary.chunk_count,
    // Honest, chunk-aware status that's CONSISTENT between the list (summary only) and the detail
    // (status tree). Two corrections the raw lifecycle/rollup get wrong:
    //   • "ready/completed" but 0 chunks embedded = nothing is actually retrievable → show Failed.
    //   • lifecycle "error" but some chunks DID embed = a partial success, not a total failure →
    //     show "Ready (partial)" (completed_with_errors), matching what the detail rollup shows.
    embedding_status: kbEmbeddingStatus(summary, tree),
    // Failure cause for the "why" tooltip. The tree's KB-level error_detail is the first errored
    // DOCUMENT's reason — the RIGHT cause for "completed_with_errors" (a few failed pages, no failed
    // SOURCE). But for a fully FAILED KB the SOURCE-level cause is more actionable, so only let the
    // document-level reason win when the rollup is completed_with_errors; otherwise prefer the errored
    // source, then the document-level reason as a fallback, then the summary's first-failure field.
    error_detail:
      (tree?.embedding_status === 'completed_with_errors' ? tree?.error_detail : null) ??
      sources.find(
        (s) =>
          s.embedding_status === 'failed' ||
          (s.embedding_status as DocumentStatus) === 'completed_with_errors',
      )?.error_detail ??
      tree?.error_detail ??
      summary.error_detail ??
      null,
    sources,
    documents: sources.flatMap((s) => s.documents),
  }
}

// ── crawl preview → console shape ────────────────────────────────────────────────────────────

export const ragCrawlStatusToConsole = (r: RagCrawlStatusResponse): RCrawlSiteStatus => ({
  // Console only models in_progress | completed; map every terminal state (incl. failed/cancelled)
  // to completed so the spinner stops and whatever pages arrived are shown.
  status: r.status === 'in_progress' ? 'in_progress' : 'completed',
  pages: (r.pages ?? []).map((p) => ({
    id: p.id,
    url: p.url,
    title: p.title,
    markdown: p.markdown,
  })),
})

// ── console request → rag request ──────────────────────────────────────────────────────────────

const mapAppData = (
  items: KBDocument[] | KBAppDocument[] | null | undefined,
): RagAppData[] | null => {
  if (!items?.length) return null
  return (items as KBAppDocument[])
    .filter((d) => 'id' in d && (d.type === 'article' || d.type === 'collection'))
    .map((d) => ({ type: d.type, id: d.id }))
}

// Build the rag SourceSpec[] from a console source block (shared by create + add-source).
const buildSources = (
  source: CreateKBSource,
  type: KBDocumentSource,
  url_selection_type: 'all' | 'selected',
  crawlJobId?: string,
): RagSourceSpec[] => {
  const cfg = source.config

  if (type === KBDocumentSource.APP) {
    return [
      {
        type: 'app',
        ref: cfg.app_source ?? '',
        app_source: (cfg.app_source ?? undefined) as RagMcpSource | undefined,
        app_source_id: cfg.app_source_id ?? null,
        app_data: mapAppData(cfg.app_data),
      },
    ]
  }

  if (type === KBDocumentSource.URL) {
    // "selected" → ingest the picked pages from the finished crawl Preview (rag reads them from the
    // job's cache, no re-crawl). One url source carrying the crawl_job_id + the picked page ids.
    if (url_selection_type === 'selected' && source.documents?.length) {
      return [
        {
          type: 'url',
          ref: cfg.url,
          crawl_subpages: false,
          crawl_job_id: crawlJobId ?? null,
          page_ids: (source.documents as KBDocument[]).map((d) => d.document_id),
        },
      ]
    }
    // "all" → crawl the seed url with the configured scope.
    return [
      {
        type: 'url',
        ref: cfg.url,
        max_pages: cfg.max_pages,
        max_depth: cfg.max_depth,
        crawl_subpages: cfg.crawl_subpages,
        include_paths: cfg.include_paths?.length ? cfg.include_paths : null,
        exclude_paths: cfg.exclude_paths?.length ? cfg.exclude_paths : null,
      },
    ]
  }

  // FILE — one file source per uploaded document (ref = uploaded file_url / storage id). Pass the
  // original filename so rag labels the document with it instead of the UUID file_url basename (the
  // uploader stores files under a UUID key; without this the doc shows e.g. "ad24…eec.pdf").
  return ((source.documents ?? []) as KBDocument[]).map((d) => ({
    type: 'file',
    ref: d.document_id,
    original_filename: d.display_name ?? null,
  }))
}

// CreateKBPayload → rag create body + the phlo_uuids the wiring layer must link in PHLO-core.
// rag's create endpoint (PR V) echo-stores the console-native settings below and derives the
// auto-resync interval from sync_period × sync_frequency (gated by sync_enabled), so the
// create→GET round-trip is lossless without a follow-up PATCH.
export const consoleCreateToRag = (
  payload: CreateKBPayload,
): { body: RagKnowledgeBaseCreateRequest; phloUuids: string[] } => ({
  body: {
    name: payload.name,
    // Driven by the create form's "Retrieval mode" dropdown (defaults to 'default'). 'docs' opts the
    // KB into rag's top-20 + long-query-rewrite profile for large documentation corpora.
    profile: payload.profile,
    sources: buildSources(
      payload.source,
      payload.type,
      payload.url_selection_type,
      payload.crawl_job_id,
    ),
    sync_period: payload.sync_period,
    sync_frequency: payload.sync_frequency,
    sync_enabled: payload.sync_enabled,
    url_selection_type: payload.url_selection_type,
    include_image_content: payload.include_image_content,
    include_youtube_content: payload.include_youtube_content,
  },
  phloUuids: payload.phlo_uuids ?? [],
})

// AddDocumentPayload → rag SourceSpec[] for POST /kb/{id}/sources (add a data source to an EXISTING
// KB). Same source-building as create; url selection is inferred (hand-picked pages → 'selected').
export const consoleAddDocumentToRag = (payload: AddDocumentPayload): RagSourceSpec[] => {
  const source = payload.source
  if (!source) return []
  const urlSelection = source.documents?.length ? 'selected' : 'all'
  return buildSources(source, source.source_type, urlSelection, payload.crawl_job_id ?? undefined)
}

// Partial<RKnowledgeBase> edit → rag PATCH body. phlo_uuids is handled by the wiring layer via
// PHLO-core, not here.
export const consoleUpdateToRag = (
  patch: Partial<RKnowledgeBase>,
): RagUpdateKnowledgeBaseRequest => {
  const body: RagUpdateKnowledgeBaseRequest = {}
  if (patch.name !== undefined) body.name = patch.name
  if (patch.enabled !== undefined) body.enabled = patch.enabled
  if (patch.sync_period !== undefined) body.sync_period = patch.sync_period
  if (patch.sync_frequency !== undefined) body.sync_frequency = patch.sync_frequency
  if (patch.sync_enabled !== undefined) body.sync_enabled = patch.sync_enabled
  if (patch.include_image_content !== undefined)
    body.include_image_content = patch.include_image_content
  if (patch.include_youtube_content !== undefined)
    body.include_youtube_content = patch.include_youtube_content
  return body
}

// ── connected-app (MCP) tree: rag-native nodes → console nodes ───────────────────────────────────
// rag types the fetch tree with nullable/optional title+content (and an extra `parent` field the
// console picker ignores); the console McpArticle requires non-null title/content. Coalesce nulls so
// the picker renders, rather than casting and shipping `null` where a string is expected.
export const ragMcpArticleToConsole = (a: RagMcpArticle): McpArticle => ({
  type: 'article',
  id: a.id,
  title: a.title ?? '',
  url: a.url ?? null,
  content: a.content ?? '',
  content_type: a.content_type,
})

export const ragMcpCollectionToConsole = (c: RagMcpArticleCollection): McpArticleCollection => ({
  type: 'collection',
  id: c.id,
  title: c.title ?? undefined,
  description: c.description ?? undefined,
  url: c.url ?? undefined,
  items: (c.items ?? []).map((n) =>
    n.type === 'collection' ? ragMcpCollectionToConsole(n) : ragMcpArticleToConsole(n),
  ),
})
